import { ReactNode } from "react";

/** Shared tone + icon vocabulary for the Home archetypes.
 *
 *  Lifted verbatim out of the old single `pages/Home.tsx` so the four
 *  role-specific homes render an amber tile or a phone glyph identically.
 *  Nothing here is Home-specific in principle — it is just where the language
 *  was first defined. */

export type Tone = "amber" | "rose" | "emerald" | "accent";

export const TONE_TEXT: Record<Tone, string> = {
  amber: "text-amber-300",
  rose: "text-rose-300",
  emerald: "text-emerald-300",
  accent: "text-accent-300",
};

export const TONE_CHIP: Record<Tone, string> = {
  amber: "bg-amber-500/10 text-amber-300",
  rose: "bg-rose-500/10 text-rose-300",
  emerald: "bg-emerald-500/10 text-emerald-300",
  accent: "bg-accent-500/10 text-accent-300",
};

/** Severity order, worst first. The old Needs-you grid pushed tiles in source
 *  order and never sorted, so the rose (most severe) tile rendered last. */
export const TONE_RANK: Record<Tone, number> = {
  rose: 0,
  amber: 1,
  accent: 2,
  emerald: 3,
};

/** Row glyphs. Line icons rather than emoji so they inherit the tone colour
 *  and match the sidebar's icon language (emoji render at whatever colour and
 *  weight the OS font decides). */
export type IconName =
  | "phone"
  | "percent"
  | "clock"
  | "x"
  | "circle"
  | "sprout"
  | "flame"
  | "bell"
  | "check"
  | "search"
  | "users"
  | "list"
  | "handshake";

const ICON_PATHS: Record<IconName, ReactNode> = {
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />,
  percent: (
    <>
      <path d="m19 5-14 14" />
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  x: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m15 9-6 6M9 9l6 6" />
    </>
  ),
  circle: <circle cx="12" cy="12" r="8.5" strokeDasharray="3 3" />,
  sprout: (
    <>
      <path d="M12 21V11" />
      <path d="M12 11C12 7.7 9.8 5 6 5c0 3.3 2.2 6 6 6Z" />
      <path d="M12 13c0-2.8 1.9-5 5-5 0 2.8-2.2 5-5 5Z" />
    </>
  ),
  flame: <path d="M12 22a7 7 0 0 0 7-7c0-4-3-6-4.5-9.5C13 8 11 8.5 9.5 10 8 8.5 8 6.5 8 6.5A9.4 9.4 0 0 0 5 15a7 7 0 0 0 7 7Z" />,
  bell: (
    <>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </>
  ),
  check: <path d="m5 13 4 4L19 7" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  users: (
    <>
      <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
      <circle cx="9" cy="7" r="3.5" />
      <path d="M22 20v-1.5a4 4 0 0 0-3-3.9" />
      <path d="M16 3.6a4 4 0 0 1 0 7.8" />
    </>
  ),
  list: (
    <>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
    </>
  ),
  handshake: (
    <>
      <path d="m11 17 2 2a1.4 1.4 0 0 0 2-2" />
      <path d="m13 15 2.5 2.5a1.4 1.4 0 0 0 2-2L15 13" />
      <path d="M3 11 8 6l3 2.5a2 2 0 0 0 2.5 0L16 6l5 5" />
      <path d="M3 11v4l3 3" />
    </>
  ),
};

export function RowIcon({ name, size = 17 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

/** The chevron every Home card ends with. */
export function Chevron({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-slate-600 group-hover:text-slate-300 group-hover:translate-x-0.5 transition flex-shrink-0"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
